import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Save, RefreshCw, Building, Navigation, Clock, CreditCard, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs font-medium">{label}</Label>
    {hint && <p className="text-[10px] text-zinc-600 mb-1">{hint}</p>}
    {children}
  </div>
);

const TF = ({ value, onChange, placeholder, type = "text" }) => (
  <Input 
    type={type}
    value={value} 
    onChange={onChange} 
    placeholder={placeholder}
    className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white text-sm mt-1" 
  />
);

export default function LocalSeoManager() {
  const [SC, setSC] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-config`);
      setSC(res.data || {});
    } catch { 
      toast.error("Failed to load local SEO config"); 
    } finally { 
      setLoading(false); 
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-config`, SC, { withCredentials: true });
      toast.success("✅ Local SEO details saved to website!");
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Failed to save: " + err.message); 
    } finally { 
      setSaving(false); 
    }
  };

  const upd = (key, val) => setSC(prev => ({ ...prev, [key]: val }));
  
  const f = (key, placeholder) => (
    <TF value={SC[key] || ""} onChange={e => upd(key, e.target.value)} placeholder={placeholder} />
  );

  if (loading) return (
    <div className="py-20 flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-zinc-500 text-xs">Loading Local SEO Profile...</p>
    </div>
  );

  return (
    <div className="space-y-6 pb-20">
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          Local SEO Profile
        </h3>
        <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
          Manage how your business appears on Google Maps and Local Search. These details automatically generate the <code>LocalBusiness</code> JSON-LD schema on your website.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Basic Business Info */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden shadow-xs">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/[0.04] flex items-center gap-2">
            <Building className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h4 className="text-gray-900 dark:text-white font-semibold text-sm">Business Identity</h4>
          </div>
          <CardContent className="pt-5 space-y-4">
            <Field label="Official Business Name" hint="How you appear on Google Maps">
              {f("local_seo_business_name", "JamaDhan Stock Market Institute")}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Public Phone Number">
                {f("local_seo_phone", "+91 92570 59361")}
              </Field>
              <Field label="Public Email">
                {f("local_seo_email", "contactus@jamadhan.com")}
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Location & Address */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden shadow-xs">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/[0.04] flex items-center gap-2">
            <Navigation className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h4 className="text-gray-900 dark:text-white font-semibold text-sm">Location</h4>
          </div>
          <CardContent className="pt-5 space-y-4">
            <Field label="Street Address">
              {f("local_seo_street", "e.g., 123 Main St")}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City">
                {f("local_seo_city", "Jaipur")}
              </Field>
              <Field label="State / Region">
                {f("local_seo_region", "Rajasthan")}
              </Field>
              <Field label="Postal / ZIP Code">
                {f("local_seo_postal", "302001")}
              </Field>
              <Field label="Country Code">
                {f("local_seo_country", "IN")}
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-white/[0.04]">
              <Field label="Latitude" hint="e.g. 26.9124">
                {f("local_seo_latitude", "26.9124")}
              </Field>
              <Field label="Longitude" hint="e.g. 75.7873">
                {f("local_seo_longitude", "75.7873")}
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* Operation Details */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden shadow-xs">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/[0.04] flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h4 className="text-gray-900 dark:text-white font-semibold text-sm">Operations</h4>
          </div>
          <CardContent className="pt-5 space-y-4">
            <Field label="Opening Hours" hint="Format: Mo-Su 09:00-18:00 (Standard schema format)">
              {f("local_seo_opening_hours", "Mo-Su 09:00-18:00")}
            </Field>
            <Field label="Price Range" hint="Google LocalBusiness uses symbols like ₹₹ or $$">
              {f("local_seo_price_range", "₹₹")}
            </Field>
          </CardContent>
        </Card>

      </div>

      <div className="flex justify-end pt-4">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl transition-all px-8 h-10">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving Profile...</> : <><Save className="w-4 h-4 mr-2" />Save Local SEO Profile</>}
        </Button>
      </div>
    </div>
  );
}
